import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type {
  DrawCustomMode,
  DrawCustomModeThis,
  DrawFeature,
  DrawLineString,
  DrawPoint,
} from '@mapbox/mapbox-gl-draw';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { isPointOnLineString } from '@racemap/utilities/functions/validation';
import { GeoElementTypes, type PointOnLineStringObject } from '@racemap/utilities/types/geos';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import type { BBox, Feature, Point, Position } from 'geojson';

// `@types/mapbox__mapbox-gl-draw` declares `featuresAt(event: Event, bbox: BBox, ...)`, but
// mapbox-gl-draw passes its own `MapMouseEvent` and you supply *either* an event or a bbox —
// the other is null. Its bbox is also a pair of screen-space corners, not a geographic BBox.
// Merged in as an extra overload rather than casting at each call site below.
declare module '@mapbox/mapbox-gl-draw' {
  interface DrawCustomModeThis {
    featuresAt(
      event: MapMouseEvent | null,
      bbox: BBox | Array<Array<number>> | null,
      bufferType: 'click' | 'tap',
    ): Array<DrawFeature>;
  }
}

interface State {
  lineStringId: string;
  lineString: MapboxDraw.DrawFeature;
  nearPointIds: Set<string>;
  usedCoordinateIndexes: Array<number>;
  coordinatePoints: Array<DrawPoint>;
  selectedPoint?: PointOnLineStringObject;
  directPoint: DrawPoint | null;
}
interface Options {
  lineStringId: string;
  usedCoordinateIndexes: Array<number>;
  pointOnLineString?: PointOnLineStringObject;
}
interface InternalPointProperties {
  active: boolean;
  id: string;
  meta: 'feature';
  'meta:type': 'Point';
  mode: 'point_on_line_string';
  user_isNearPoint?: boolean;
  user_lineStringId: string;
  user_coordinateIndex: number;
  user_isIntermediated: boolean;
}
interface PointProperties {
  isNearPoint?: boolean;
  lineStringId?: string;
  coordinateIndex: number;
  isIntermediated?: boolean;
}
type PointOnLineStringInternal = Feature<Point, InternalPointProperties>;
type CoordinatePointGeoJSON = Feature<
  Point,
  InternalPointProperties & { user_isIntermediated: true }
>;
type CoordinatePoint = DrawPoint & { properties: PointProperties };

type DrawPointOnLineStringModeType = DrawCustomMode<State, Options> & {
  isDrawLineString: (feature?: DrawFeature) => feature is DrawLineString;
  isDrawPoint: (feature: DrawFeature) => feature is DrawPoint;
  isCoordinatePointGeoJSON: (feature: unknown) => feature is CoordinatePointGeoJSON;
  isPointOnLineString: (feature: unknown) => feature is PointOnLineStringInternal;
  isCoordinatePoint(element: DrawFeature): element is CoordinatePoint;
  createPoint: (
    this: DrawCustomModeThis & DrawPointOnLineStringModeType,
    coordinate: Position,
    properties: { coordinateIndex?: number; isIntermediated?: boolean; isDirectPoint?: boolean },
    id?: string,
  ) => DrawPoint;
  createNearPoint: (
    this: DrawCustomModeThis & DrawPointOnLineStringModeType,
    coordinate: Position,
    properties: { coordinateIndex: number; isIntermediated: boolean },
    id?: string,
  ) => DrawPoint;
  createDirectPoint: (
    this: DrawCustomModeThis & DrawPointOnLineStringModeType,
    coordinate: Position,
    properties: { isDirectPoint: boolean },
    id?: string,
  ) => DrawPoint;
};

export const PointOnLineStringMode: DrawPointOnLineStringModeType = {
  onSetup: function (opts) {
    const lineString = this.getFeature(opts.lineStringId);
    if (!this.isDrawLineString(lineString)) {
      throw new Error('Selected feature is not a LineString');
    }

    const possibleCoordinates = lineString.getCoordinates();
    const coordinatePoints = [];

    for (const [index, coordinate] of possibleCoordinates.entries()) {
      if (opts.usedCoordinateIndexes.includes(index)) continue;

      const point = this.createNearPoint(coordinate, {
        coordinateIndex: index,
        isIntermediated: true,
      });
      coordinatePoints.push(point);
    }

    const state: State = {
      lineStringId: opts.lineStringId,
      lineString,
      usedCoordinateIndexes: opts.usedCoordinateIndexes,
      nearPointIds: new Set(),
      coordinatePoints,
      selectedPoint: opts.pointOnLineString,
      directPoint: null,
    };

    return state;
  },

  onClick: function (state, event) {
    const targetFeatures = this.featuresAt(event, null, 'click');
    const coordinatePoint = (targetFeatures as Array<unknown>).find(
      (f): f is CoordinatePointGeoJSON => this.isCoordinatePointGeoJSON(f),
    );
    const lineStringMap = targetFeatures.find((f) => this.isDrawLineString(f));
    const lineString =
      lineStringMap?.properties?.id != null ? this.getFeature(lineStringMap.properties.id) : null;

    // could be also undefined
    const point =
      coordinatePoint?.properties?.id != null
        ? this.getFeature(coordinatePoint.properties.id)
        : null;

    // user clicked on a linestring coordinate. create the point on this position
    if (point != null && this.isCoordinatePoint(point)) {
      point.properties.isIntermediated = undefined;
      point.properties.isNearPoint = undefined;
      point.properties.lineStringId = state.lineStringId;

      if (state.selectedPoint == null) {
        this.map.fire('draw.create', {
          features: [point.toGeoJSON()],
        });
      } else {
        const intermediatePoint = point.toGeoJSON();
        if (!isPointOnLineString(intermediatePoint)) return;

        const updatedPoint = {
          ...state.selectedPoint,
          geometry: intermediatePoint.geometry,
          properties: {
            ...state.selectedPoint.properties,
            coordinateIndex: intermediatePoint.properties.coordinateIndex,
          },
        };
        this.map.fire('draw.update', {
          features: [updatedPoint],
          action: 'move',
        });
      }
      this.map.fire('draw.modechange', { mode: 'simple_select', featureIds: [point.id] });

      state.coordinatePoints.forEach(
        (p) => p.id !== point.id && this.deleteFeature(p.id.toString(), { silent: true }),
      );
      return;
    }

    // user clicked on linestring. create a point on the nearest position
    if (lineString != null && this.isDrawLineString(lineString)) {
      const nearesPointOnLineString = nearestPointOnLine(
        { type: 'LineString', coordinates: state.lineString.getCoordinates() as Array<Position> },
        [event.lngLat.lng, event.lngLat.lat],
      );
      if (
        nearesPointOnLineString == null ||
        nearesPointOnLineString.properties.index == null ||
        state.directPoint == null
      )
        return;

      lineString.addCoordinate(
        nearesPointOnLineString.properties.index + 1,
        nearesPointOnLineString.geometry.coordinates[0],
        nearesPointOnLineString.geometry.coordinates[1],
      );

      const updatedLineString = lineString.toGeoJSON();

      this.map.fire('draw.update', {
        features: [updatedLineString],
        action: 'change_coordinates',
        coordinateAction: 'add',
        coordinates: [
          {
            index: nearesPointOnLineString.properties.index + 1,
            lineStringId: state.lineStringId,
            lat: nearesPointOnLineString.geometry.coordinates[1],
            lng: nearesPointOnLineString.geometry.coordinates[0],
          },
        ],
      });

      if (state.selectedPoint == null) {
        const createdPoint = {
          type: 'Feature',
          geometry: nearesPointOnLineString.geometry,
          properties: {
            lineStringId: state.lineStringId,
            coordinateIndex: nearesPointOnLineString.properties.index + 1,
          },
        };
        this.map.fire('draw.create', {
          features: [createdPoint],
        });
      } else {
        const updatedPoint = {
          ...state.selectedPoint,
          geometry: nearesPointOnLineString.geometry,
          properties: {
            ...state.selectedPoint.properties,
            coordinateIndex: nearesPointOnLineString.properties.index + 1,
          },
        };
        this.map.fire('draw.update', {
          features: [updatedPoint],
          action: 'move',
        });
      }

      this.map.fire('draw.modechange', { mode: 'simple_select' });

      state.coordinatePoints.forEach((p) => this.deleteFeature(p.id.toString(), { silent: true }));
      return;
    }
  },

  onStop: function (state) {
    state.coordinatePoints.forEach((p) => this.deleteFeature(p.id.toString(), { silent: true }));
  },

  onMouseMove: function (state, event) {
    this.updateUIClasses({ mouse: 'none' });

    const bbox = [
      [event.point.x - 10, event.point.y - 10],
      [event.point.x + 10, event.point.y + 10],
    ];

    // intermediated point or linestring is in click range. mark as point!
    const targetFeatures = this.featuresAt(event, null, 'click');
    const intermediatedPointDirect = targetFeatures.find((f) => this.isCoordinatePointGeoJSON(f));
    const lineStringDirect = targetFeatures.find((f) => this.isDrawLineString(f));

    // point is over a other linestring
    if (lineStringDirect != null && lineStringDirect.properties?.id !== state.lineStringId) return;

    if (intermediatedPointDirect || lineStringDirect) {
      this.updateUIClasses({ mouse: 'pointer' });
    }

    // feature is in the bbox range.
    const nearFeatures = this.featuresAt(null, bbox, 'click');

    // highlight the coordinates of the linestring
    const newNearPointIds = new Set<string>(
      (nearFeatures as Array<unknown>)
        .filter((f): f is CoordinatePointGeoJSON => this.isCoordinatePointGeoJSON(f))
        .map((f) => f.properties.id),
    );
    state.nearPointIds = newNearPointIds;
    for (const pointId of newNearPointIds) {
      this.doRender(pointId);
    }

    // if no coordinates in range but the line string, then create point on the
    // nearest position on the line string
    const lineStringInRange = nearFeatures.some(
      (f) => this.isDrawLineString(f) && state.lineStringId === f?.properties?.id,
    );

    if (newNearPointIds.size === 0 && lineStringInRange) {
      const nearesPointOnLineString = nearestPointOnLine(
        { type: 'LineString', coordinates: state.lineString.getCoordinates() as Array<Position> },
        [event.lngLat.lng, event.lngLat.lat],
      );

      if (state.directPoint == null) {
        state.directPoint = this.createDirectPoint(nearesPointOnLineString.geometry.coordinates, {
          isDirectPoint: true,
        });
      } else {
        state.directPoint.setCoordinates(nearesPointOnLineString.geometry.coordinates);
        this.doRender(state.directPoint.id.toString());
      }
    } else if (state.directPoint != null) {
      this.deleteFeature(state.directPoint.id.toString(), { silent: true });
      state.directPoint = null;
    }
  },

  isDrawLineString: (feature?: MapboxDraw.DrawFeature): feature is DrawLineString => {
    return feature?.type === 'LineString' || feature?.properties?.['meta:type'] === 'LineString';
  },

  isDrawPoint: (feature?: MapboxDraw.DrawFeature): feature is DrawPoint => {
    return feature?.type === 'Point' || feature?.properties?.['meta:type'] === 'Point';
  },

  isCoordinatePointGeoJSON(element: unknown): element is CoordinatePointGeoJSON {
    if (
      element == null ||
      typeof element !== 'object' ||
      !('geometry' in element) ||
      !('properties' in element)
    )
      return false;
    if (
      typeof element.geometry !== 'object' ||
      element.geometry == null ||
      !('type' in element.geometry) ||
      element.geometry.type !== GeoElementTypes.Point
    )
      return false;
    if (
      typeof element.properties !== 'object' ||
      element.properties == null ||
      !('user_isIntermediated' in element.properties) ||
      element.properties.user_isIntermediated !== true
    )
      return false;

    return true;
  },

  isPointOnLineString(element: unknown): element is PointOnLineStringInternal {
    if (element == null || typeof element !== 'object') return false;
    const e = element as Record<string, unknown>;
    const geometry = e.geometry as Record<string, unknown> | undefined;
    const properties = e.properties as Record<string, unknown> | undefined;
    return (
      geometry?.type === GeoElementTypes.Point && isNotEmptyString(properties?.user_coordinateIndex)
    );
  },

  isCoordinatePoint(element: DrawFeature): element is CoordinatePoint {
    return (
      element.type === GeoElementTypes.Point &&
      (element.properties as PointProperties | null)?.isIntermediated != null
    );
  },

  createNearPoint: function (coordinates, properties, id) {
    return this.createPoint(coordinates, properties, id);
  },

  createDirectPoint: function (coordinates, properties, id) {
    return this.createPoint(coordinates, properties, id);
  },

  createPoint: function (coordinates, properties, id) {
    const point = this.newFeature({
      type: 'Feature',
      properties,
      id,
      geometry: {
        type: 'Point',
        coordinates,
      },
    });
    this.addFeature(point);

    return point as DrawPoint;
  },

  toDisplayFeatures: function (state, geojson, display) {
    if (this.isPointOnLineString(geojson) && !geojson.properties.user_isIntermediated) {
      return;
    }
    if (this.isCoordinatePointGeoJSON(geojson)) {
      // this.updateUIClasses({ mouse: 'pointer' });
      geojson.properties.user_isNearPoint = state.nearPointIds.has(geojson.properties.id);
    }
    display(geojson);
  },
};
